import { lazy } from 'react';

// Single home of the stale-chunk recovery policy (CON-249). A fresh deploy
// produces new content-hashed chunk filenames; a stale app shell still
// references the old names, so dynamic imports 404. Recovery is one
// automatic full reload PER BUILD: the flag is keyed by the running
// bundle's content hash, and a reload under a stale shell boots the NEW
// build, so every deploy re-arms automatic healing by construction (a
// long-lived tab that hits a second deploy days later still self-heals).
// A shell that STAYS stale (an edge caching it despite no-cache) burns its
// single attempt and surfaces the manual screen instead of looping.

const RELOAD_PREFIX = 'chunk-reload-attempted';

export const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' ||
  /Loading (CSS )?chunk \S+ failed/i.test(error?.message || '');

// The running build's identity, read from the main bundle's content-hashed
// filename in the DOM (/static/js/main.<hash>.js).
const buildId = () => {
  try {
    const src = Array.from(document.scripts || [])
      .map((script) => script.src || '')
      .find((s) => /\/static\/js\/main\.[^/]+\.js/.test(s));
    const match = src && /main\.([\w.]+)\.js/.exec(src);
    return match ? match[1] : 'unknown-build';
  } catch {
    return 'unknown-build';
  }
};

// A user explicitly asking to reload starts the incident over: forget the
// consumed auto-reload so the next failure heals itself again.
export const clearChunkReloadFlags = () => {
  try {
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith(RELOAD_PREFIX))
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
};

let attemptedThisPageLoad = false;

// The shared policy: if this error is a stale-chunk failure and this build
// has not already consumed its automatic reload, mark it and reload.
// Returns true when a reload was triggered (callers should stop rendering).
export const attemptChunkReload = (error) => {
  if (!isChunkLoadError(error)) return false;
  if (attemptedThisPageLoad) return false;
  try {
    const key = `${RELOAD_PREFIX}:${buildId()}`;
    if (window.sessionStorage.getItem(key) === '1') return false;
    window.sessionStorage.setItem(key, '1');
  } catch {
    // sessionStorage blocked (private browsing). The module flag below still
    // bounds THIS page load to one reload, but it cannot survive the reload
    // itself, so a permanently broken chunk reloads once per page load
    // there - a slow crawl, not a tight loop.
  }
  attemptedThisPageLoad = true;
  window.location.reload();
  return true;
};

// Wraps React.lazy so a missing chunk after a deploy doesn't blow up the
// app: retry the import once for transient blips, then hand the failure to
// the shared reload policy, and only surface the error (to the
// ErrorBoundary) once this build's automatic reload is spent.
const lazyWithRetry = (factory) =>
  lazy(async () => {
    try {
      return await factory();
    } catch (firstError) {
      try {
        return await factory();
      } catch (secondError) {
        if (attemptChunkReload(secondError)) {
          // Return a never-resolving promise so React doesn't surface the
          // error to the ErrorBoundary before the reload takes effect.
          return new Promise(() => {});
        }
        throw secondError;
      }
    }
  });

export default lazyWithRetry;
